"use client";
import React, { useEffect, useState } from "react";
import style from './select.module.scss';
import Image from 'next/image';
import Chevron from "@/components/common/svg/Chevron";
import { useTranslations } from "next-intl";
import { BackgroundColors, FontFamily } from "@/projectRoot/src/utils/models/globalTypes";
export interface IdOption {
    id: string;
    name: string;
    condition_value?: {
        show: boolean;
    };
    type?: string;
    translated_name?: string;
}

export interface SelectProps {
    idOptions?: IdOption[];
    selectedOption: string | undefined;
    preSelectedItem?: any;
    placeholderText?: string;
    children?: React.ReactNode;
    classes?: string[];
    fontFamily?: FontFamily;
    backgroundColor?: BackgroundColors;
    activeBackground?: BackgroundColors;
    hasBorder?: boolean;
    onSelect: (option: any) => void;
    onClick?: () => void;
    onBlur?: () => void;
    onFocus?: () => void;
    bottomOpen?: boolean;
    label?: string;
    id?: string;
    useTranslation?: boolean;
    translatableOptions?: boolean;
    customStyle?: React.CSSProperties;
    error?: { has_error: boolean, message: string };
    enabled?: boolean;
    translationsPrefix?: string;
    searchable?: boolean;
    formClicked?: boolean;
    currentOpened?: string | null;
    setCurrentOpened?: (id?: string) => void;
}

export const Select = ({
    idOptions,
    selectedOption,
    preSelectedItem,
    placeholderText,
    children,
    classes = [],
    fontFamily = FontFamily.MUNDIAL_REGULAR,
    backgroundColor = BackgroundColors.TRANSPARENT,
    activeBackground,
    hasBorder = true,
    onSelect = () => { },
    onClick,
    onBlur,
    onFocus,
    bottomOpen = true,
    label,
    id,
    useTranslation = true,
    translatableOptions = false,
    customStyle,
    error = { has_error: false, message: '' },
    enabled = true,
    translationsPrefix,
    searchable = false,
    formClicked = true,
    setCurrentOpened,
    currentOpened
}: SelectProps) => {
    const t = useTranslations();
    const [opened, setOpened] = useState<boolean>(false);
    const [showInput, setShowInput] = useState<boolean>(false);
    const [filterOptions, setFilterOptions] = useState<string>('');
    // const [selected, setSelected] = useState<string>('');
    
    const selectIdOption = (option: IdOption) => {
        if(!enabled || (option.condition_value && !option.condition_value.show)) return;
        onSelect(option.id);
        setOpened(false);
        setShowInput(false);
        setFilterOptions('');
        if(setCurrentOpened){
            setCurrentOpened('');
        }
    }

    const handleClick = () => {
        if(!enabled) return;
        // setOpened(!opened);
        if(onClick){
            onClick();
        }
        setShowInput(!opened);
        if(setCurrentOpened && id){
            setCurrentOpened(id);
        }else{
            setOpened(!opened);
        }
        if(currentOpened && id && currentOpened == id){
            setCurrentOpened('');
        }
    }

    const onInputFilterOptions = (value: string) => {
        setFilterOptions(value.trim());
    }

    const getDisplayLabel = () => {
        // Handle the Empty State
        if (!selectedOption) {
            return useTranslation ? t(placeholderText ?? '') : placeholderText;
        }

        // Handle Translatable Options
        if (translatableOptions) {
            return t(translationsPrefix ? `${translationsPrefix}.${selectedOption}` : selectedOption);
        } else {
            return idOptions.find((op) => op.id == selectedOption)?.name;
        }
    };

    // useEffect(() => {
    //     setSelected(preSelectedItem && options.find(item => item.text === preSelectedItem) ? preSelectedItem : '');
    // }, [options])

    useEffect(() => {
        if (typeof window !== "undefined") {
            window.addEventListener('click', () => {setOpened(false); setShowInput(false); setFilterOptions(''); setCurrentOpened ? setCurrentOpened('') : () => {};});
            return () => {
                window.removeEventListener('click', () => {setOpened(false); setShowInput(false); setFilterOptions(''); setCurrentOpened ? setCurrentOpened('') : () => {};});
            };
        }
    }, []);
    
    // verovatno je nesto bilo za contract-e na mtel-u ???
    // useEffect(() => {
    //     const activeElement = document.querySelector(`.${style.active}`);
    //     if (activeElement instanceof HTMLElement) {
    //         activeElement.style.backgroundColor = activeBackground;
    //     }
    // }, [selectedOption]);
    
    useEffect(() => {
        setOpened(false);
        setShowInput(false);
        setFilterOptions('');
        
    }, [formClicked]);

    useEffect(() => {
        if(currentOpened && currentOpened !== null && id && id !== null){
            setOpened(currentOpened == id);
        }else{
            setOpened(false);
        }
    }, [currentOpened, id]);

    return (
        <div className={`
            ${style.select} 
            ${style[backgroundColor]} 
            ${!hasBorder ? style.noBorder : ''} 
            ${error.has_error ? style.error : ''}
            ${classes.join(' ')}
            `}
            id={id}
            style={customStyle}
        >
            <div
                className={`${style.selectBtn} ${!enabled ? style.disabled : ''} ${opened ? style.focused : ''}`}
                onClick={(e) => { e.stopPropagation(); handleClick(); }}
                onBlur={() => {onBlur ? onBlur() : () => {}}}
                onFocus={onFocus}
                tabIndex={0}
                onKeyDown={(e) => e.key == 'Enter' ? handleClick() : () => {}}
            >
                <span className={style.text}>
                    {getDisplayLabel()}
                    {
                        searchable && showInput
                        ?
                        <input 
                            type="text" 
                            value={filterOptions} 
                            onInput={(e) => onInputFilterOptions(e.currentTarget.value)}
                            autoFocus={showInput && (currentOpened == id || opened)}
                            className={style.searchInput}
                        />
                        : <></>
                    }
                </span>
                <span className={`
                    ${style.dropdownArrow} 
                    ${opened ? style.rotate : ''} 
                    ${!bottomOpen ? style.top : ''}`
                }>
                    <Chevron width={13} height={8} />
                </span>
            </div>
            {
                label ?
                    <label className={`${style.label} ${!enabled ? style.disabled : ''}`} dangerouslySetInnerHTML={{__html: label}} onClick={(e) => { e.stopPropagation(); handleClick(); }}>
                        {label}
                    </label>
                    : <></>
            }
            {
                children ? children : <></>
            }
            <div className={`${style.selectContent} ${opened || (idOptions && currentOpened && id ? currentOpened == id : false) ? style.visible : ''} ${!bottomOpen ? style.top : ''}`}>
                {
                    idOptions ?
                    idOptions.filter(option => searchable ? option[option.translated_name ? 'translated_name' : 'name'].toLowerCase().includes(filterOptions.toLowerCase()) : true).map(option => {
                        return <div
                            key={option.id}
                            className={`
                                ${style.selectItem}
                                ${option.id == selectedOption ? style.active : ''}`}
                            onClick={() => selectIdOption(option)}
                            tabIndex={opened ? 0 : -1}
                            onKeyDown={(e) => e.key == 'Enter' ? selectIdOption(option) : () => {}}
                        >
                            {translatableOptions ? t(translationsPrefix ? `${translationsPrefix}.${option.id}` : option.id) : option.name}
                        </div>
                    })
                    : 
                    <></>
                }
            </div>
            <p className="errorMessage" style={{width: '100%', minWidth: '100%'}}>
                {error.message}
            </p>
        </div >
    )
}

export default Select;